<template>
  <v-app>
    <!-- Auf der Login-Seite nur Router-View anzeigen -->
    <template v-if="isLoginPage">
      <router-view />
    </template>

    <!-- Hauptlayout mit modernisiertem Menü -->
    <template v-else>
      <!-- Desktop-Modus Toggle -->
      <v-btn
        v-if="!isLoginPage"
        icon
        class="desktop-toggle"
        @click="toggleDesktopMode"
      >
        <v-icon>{{ isDesktopMode ? 'mdi-monitor' : 'mdi-desktop-classic' }}</v-icon>
      </v-btn>

      <!-- Desktop-Ansicht -->
      <DesktopView v-if="isDesktopMode" v-model:desktopMode="isDesktopMode" />

      <!-- Normale Ansicht -->
      <template v-else>
        <!-- Animierte Hintergrundelemente -->
        <div class="background-elements">
          <div class="ambient-light ambient-light-1"></div>
          <div class="ambient-light ambient-light-2"></div>
          <div class="ambient-light ambient-light-3"></div>
          <div class="grid-overlay"></div>
        </div>

        <!-- Desktop-Navigation: Schwebend mit Glass-Effekt -->
        <template v-if="!isMobile">
          <v-app-bar
            app
            :elevation="scrolled ? 2 : 0"
            class="floating-navbar"
            :class="{ 'navbar-scrolled': scrolled, 'navbar-hidden': !showAppBar }"
          >
            <!-- Logo und Titelbereich -->
            <div class="navbar-brand">
              <div class="brand-icon">
                <v-icon color="success" size="28">mdi-shield-account</v-icon>
              </div>
              <v-toolbar-title class="app-title">ARCM Core</v-toolbar-title>
            </div>

            <v-spacer></v-spacer>

            <!-- Hauptnavigation mit modernen Icons -->
            <div class="nav-links">
              <v-btn
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :class="{ 'nav-active': isActive('/dashboard') }"
                class="nav-btn"
                @click="navigateTo('/dashboard')"
              >
                <v-icon start size="18">mdi-view-dashboard-outline</v-icon>
                Dashboard
              </v-btn>
              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer
                "
                :model-value="openDesktopMenu === 'admin'"
                @update:model-value="(val) => handleDesktopMenu('admin', val)"
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInAdminSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-shield-crown-outline</v-icon>
                    Admin
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      @click="navigateTo('/admin')"
                      :active="isActive('/admin')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-shield-crown-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Admin-Bereich</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="
                        authStore.user?.super_admin || authStore.user?.developer
                      "
                      @click="navigateTo('/management')"
                      :active="isActive('/management')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-cogs</v-icon>
                      </template>
                      <v-list-item-title>Management</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.developer"
                      @click="navigateTo('/devtools')"
                      :active="isActive('/devtools')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-alert</v-icon>
                      </template>
                      <v-list-item-title>Dev Tools</v-list-item-title>
                    </v-list-item>
                    <v-list-item
                      v-if="
                authStore.user?.admin ||
                authStore.user?.super_admin ||
                authStore.user?.developer
              "
                      @click="navigateTo('/reinigungsplan')"
                      :active="isActive('/reinigungsplan')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-broom</v-icon>
                      </template>
                      <v-list-item-title>Reinigungsplan drucken</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>

              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :model-value="openDesktopMenu === 'communication'"
                @update:model-value="
                  (val) => handleDesktopMenu('communication', val)
                "
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInCommunicationSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-message-text-outline</v-icon>
                    Kommunikation
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      @click="navigateTo('/calendar')"
                      :active="isActive('/calendar')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-calendar-month-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Kalender</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="navigateTo('/chat')"
                      :active="route.path.includes('/chat')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-chat</v-icon>
                      </template>
                      <v-list-item-title>
                        Chat
                        <v-badge
                          v-if="unreadChatMessages > 0"
                          :content="unreadChatMessages"
                          color="error"
                          dot
                          floating
                        ></v-badge>
                      </v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="navigateTo('/messages')"
                      :active="isActive('/messages')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-email-outline</v-icon>
                      </template>
                      <v-list-item-title>
                        Nachrichten
                        <v-badge
                          v-if="unreadMessages > 0"
                          :content="unreadMessages"
                          color="error"
                          inline
                        ></v-badge>
                      </v-list-item-title>
                    </v-list-item>

                    <v-divider class="my-1"></v-divider>

                    <v-list-item
                      @click="navigateTo('/lists')"
                      :active="route.path.includes('/lists')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-format-list-bulleted</v-icon>
                      </template>
                      <v-list-item-title>Dynamische Listen</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>

              <!-- Werkstatt-Dropdown mit modernem Stil -->
              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting ||
                  authStore.user?.dtc
                "
                :model-value="openDesktopMenu === 'werkstatt'"
                @update:model-value="(val) => handleDesktopMenu('werkstatt', val)"
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInWerkstattSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-tools</v-icon>
                    Werkstatt
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                      @click="navigateTo('/instandsetzung')"
                      :active="isActive('/instandsetzung')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-wrench-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Instandsetzung</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                      @click="navigateTo('/retouren')"
                      :active="isActive('/retouren')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-recycle</v-icon>
                      </template>
                      <v-list-item-title>Retouren</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!isOnlyDtc()"
                      @click="navigateTo('/retouren-sorting')"
                      :active="isActive('/retouren-sorting')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-package-variant-closed-remove</v-icon>
                      </template>
                      <v-list-item-title>Retouren Sorting</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user && (authStore.user.returen_sorting_v2 || authStore.user.admin || authStore.user.super_admin || authStore.user.developer)"
                      @click="navigateTo('/retouren-sorting-v2')"
                      :active="isActive('/retouren-sorting-v2')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="primary">mdi-package-variant-closed</v-icon>
                      </template>
                      <v-list-item-title>Retouren Sorting V2</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                      @click="navigateTo('/refurbish')"
                      :active="isActive('/refurbish')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="orange-darken-1">mdi-tools</v-icon>
                      </template>
                      <v-list-item-title>Frankreich Paletten</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.dtc || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                      @click="navigateTo('/dtc')"
                      :active="isActive('/dtc')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="teal-darken-1">mdi-package-variant-closed</v-icon>
                      </template>
                      <v-list-item-title>DTC Retouren</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                      @click="navigateTo('/b-ware')"
                      :active="isActive('/b-ware')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="amber-darken-2">mdi-quality-medium</v-icon>
                      </template>
                      <v-list-item-title>B-Ware</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                      @click="navigateTo('/b-ware-leistung')"
                      :active="isActive('/b-ware-leistung')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="amber-darken-2">mdi-counter</v-icon>
                      </template>
                      <v-list-item-title>B-Ware Leistung</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!isOnlyDtc()"
                      @click="navigateTo('/nebentaetigkeiten')"
                      :active="isActive('/nebentaetigkeiten')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-check-circle-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Nebentätigkeiten</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                      @click="navigateTo('/ersatzteile')"
                      :active="isActive('/ersatzteile')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-barcode</v-icon>
                      </template>
                      <v-list-item-title>Ersatzteile</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="!isOnlyDtc()"
                      @click="navigateTo('/iris-code-matrix')"
                      :active="isActive('/iris-code-matrix')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-table-search</v-icon>
                      </template>
                      <v-list-item-title>IRIS Code Matrix</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>

              <!-- Refurbish-Button -->
              <v-btn
                v-if="authStore.user?.refurbish && !authStore.user?.admin && !authStore.user?.super_admin && !authStore.user?.developer"
                :class="{ 'nav-active': isActive('/refurbish') }"
                class="nav-btn"
                @click="navigateTo('/refurbish')"
              >
                <v-icon start size="18">mdi-tools</v-icon>
                Refurbish
              </v-btn>

              <!-- Office-Dropdown -->
              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.office
                "
                :model-value="openDesktopMenu === 'office'"
                @update:model-value="(val) => handleDesktopMenu('office', val)"
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInOfficeSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-briefcase</v-icon>
                    Office
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      @click="navigateTo('/office')"
                      :active="isActive('/office')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="indigo-darken-1">mdi-package-check</v-icon>
                      </template>
                      <v-list-item-title>Frankreich/DTC Freigabe</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      @click="navigateTo('/kv-confirmations')"
                      :active="isActive('/kv-confirmations')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="indigo-darken-1">mdi-file-document-check-outline</v-icon>
                      </template>
                      <v-list-item-title>KV-Bestätigungen</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="
                        authStore.user?.admin ||
                        authStore.user?.super_admin ||
                        authStore.user?.developer ||
                        authStore.user?.office
                      "
                      @click="navigateTo('/inventur/auswertung')"
                      :active="isActive('/inventur/auswertung')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="indigo-darken-1">mdi-warehouse</v-icon>
                      </template>
                      <v-list-item-title>Inventur-Auswertung</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>

              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.lagerist ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting ||
                  authStore.user?.inventur
                "
                :model-value="openDesktopMenu === 'storage'"
                @update:model-value="(val) => handleDesktopMenu('storage', val)"
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInStorageSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-package-variant</v-icon>
                    Lager
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      @click="navigateTo('/commission')"
                      :active="isActive('/commission')"
                      class="dropdown-item"
                      v-if="
                        authStore.user?.admin ||
                        authStore.user?.super_admin ||
                        authStore.user?.developer ||
                        authStore.user?.lagerist
                      "
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-package-variant-closed</v-icon
                        >
                      </template>
                      <v-list-item-title>Kommissionen</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer || authStore.user?.lagerist"
                      @click="navigateTo('/request')"
                      :active="isActive('/request')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-help-circle-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Anfrage</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.lagerist || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                      @click="navigateTo('/versand')"
                      :active="isActive('/versand')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-truck-delivery</v-icon>
                      </template>
                      <v-list-item-title>Versand</v-list-item-title>
                    </v-list-item>

                    <v-list-item
                      v-if="authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer || authStore.user?.lagerist || authStore.user?.inventur"
                      @click="navigateTo('/inventur')"
                      :active="isActive('/inventur')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success">mdi-warehouse</v-icon>
                      </template>
                      <v-list-item-title>Inventur</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>

              <v-menu
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :model-value="openDesktopMenu === 'reporting'"
                @update:model-value="(val) => handleDesktopMenu('reporting', val)"
                location="bottom"
                transition="fade-transition"
              >
                <template #activator="{ props }">
                  <v-btn
                    class="nav-btn"
                    :class="{ 'nav-active': isInReportingSection() }"
                    v-bind="props"
                  >
                    <v-icon start size="18">mdi-chart-box-outline</v-icon>
                    Auswertung
                    <v-icon end size="16">mdi-chevron-down</v-icon>
                  </v-btn>
                </template>

                <v-card class="dropdown-menu">
                  <v-list density="compact">
                    <v-list-item
                      @click="navigateTo('/statistik')"
                      :active="isActive('/statistik')"
                      class="dropdown-item"
                    >
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-chart-box-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Statistik</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="promptPassword" class="dropdown-item">
                      <template v-slot:prepend>
                        <v-icon size="18" color="success"
                          >mdi-text-box-outline</v-icon
                        >
                      </template>
                      <v-list-item-title>Zusammenfassungen</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
            <v-spacer></v-spacer>

            <!-- Benutzerbereich -->
            <div class="user-section">
              <span class="user-greeting" v-if="authStore.isAuthenticated">
                {{ greeting }},
                <span class="user-name">{{ authStore.user?.loginname }}</span>
              </span>

              <!-- Nachrichten-Icon mit Badge -->
              <v-btn
                icon
                class="message-icon-btn mx-2"
                @click="navigateTo('/messages')"
                v-if="authStore.isAuthenticated"
              >
                <v-badge
                  :content="unreadMessages"
                  :model-value="unreadMessages > 0"
                  color="error"
                  overlap
                >
                  <v-icon color="white">mdi-email-outline</v-icon>
                </v-badge>
              </v-btn>

              <v-menu location="bottom end" transition="slide-y-transition">
                <template v-slot:activator="{ props }">
                  <v-btn icon class="user-avatar-btn" v-bind="props">
                    <v-badge
                      :color="
                        isAuthLoading
                          ? 'grey'
                          : getStatusColor(authStore.currentStatus)
                      "
                      dot
                      bordered
                      location="bottom end"
                    >
                      <v-avatar size="36" color="success" class="user-avatar">
                        <v-progress-circular
                          v-if="isAuthLoading"
                          indeterminate
                          color="white"
                          size="24"
                        ></v-progress-circular>
                        <span
                          v-else-if="
                            authStore.user?.vorname && authStore.user?.nachname
                          "
                        >
                          {{
                            getInitials(
                              authStore.user.vorname,
                              authStore.user.nachname
                            )
                          }}
                        </span>
                        <v-icon v-else>mdi-account</v-icon>
                      </v-avatar>
                    </v-badge>
                  </v-btn>
                </template>

                <v-card min-width="320" class="user-menu">
                  <!-- Status-Sektion: Neue, übersichtliche Liste -->
                  <div class="status-section">
                    <v-skeleton-loader
                      v-if="isAuthLoading"
                      type="card-heading"
                      class="pa-3"
                    ></v-skeleton-loader>
                    <div
                      class="status-header d-flex align-center pa-3"
                      v-else-if="authStore.user?.id"
                    >
                      <v-badge
                        :color="getStatusColor(authStore.currentStatus)"
                        dot
                        bordered
                        location="bottom end"
                        class="mr-3"
                      >
                        <v-avatar size="42" color="success">
                          <span
                            v-if="
                              authStore.user.vorname && authStore.user.nachname
                            "
                          >
                            {{
                              getInitials(
                                authStore.user.vorname,
                                authStore.user.nachname
                              )
                            }}
                          </span>
                          <v-icon v-else>mdi-account</v-icon>
                        </v-avatar>
                      </v-badge>
                      <div>
                        <div class="user-fullname">
                          {{ authStore.user.vorname || "" }}
                          {{ authStore.user.nachname || "" }}
                        </div>
                        <div class="user-status-row">
                          <v-icon
                            size="x-small"
                            :color="getStatusColor(authStore.currentStatus)"
                            class="mr-1"
                          >
                            mdi-circle
                          </v-icon>
                          <span class="status-label">{{
                            getStatusText(authStore.currentStatus)
                          }}</span>
                        </div>
                      </div>
                    </div>

                    <div v-else class="d-flex justify-center align-center pa-4">
                      <v-progress-circular
                        indeterminate
                        color="success"
                        size="24"
                      ></v-progress-circular>
                    </div>

                    <v-divider></v-divider>

                    <!-- Status-Optionen als klare vertikale Liste -->
                    <div class="status-options">
                      <v-list density="compact" class="status-list">
                        <v-list-item
                          v-for="status in statusOptions"
                          :key="status.value"
                          :active="authStore.currentStatus === status.value"
                          :class="[
                            'status-list-item',
                            authStore.currentStatus === status.value
                              ? 'status-active'
                              : '',
                          ]"
                          @click="handleStatusClick(status.value, $event)"
                        >
                          <template v-slot:prepend>
                            <v-avatar
                              size="28"
                              :color="getStatusColor(status.value)"
                              class="status-avatar mr-2"
                            >
                              <v-icon size="16" color="white">{{
                                status.icon
                              }}</v-icon>
                            </v-avatar>
                          </template>
                          <v-list-item-title>{{
                            status.label
                          }}</v-list-item-title>
                          <v-list-item-subtitle v-if="status.description">
                            {{ status.description }}
                          </v-list-item-subtitle>
                        </v-list-item>
                      </v-list>

                      <!-- Benutzerdefinierte Status-Nachricht -->
                      <div
                        class="custom-status-input px-3 pb-3"
                        v-if="authStore.currentStatus === 'custom'"
                      >
                        <v-text-field
                          v-model="customStatusMessage"
                          variant="outlined"
                          density="compact"
                          label="Status-Nachricht"
                          hide-details
                          class="mt-2"
                          color="success"
                          bg-color="rgba(30, 30, 40, 0.5)"
                          append-inner-icon="mdi-check"
                          @keyup.enter="updateCustomStatus"
                          @click:append-inner.stop="updateCustomStatus"
                          @click.stop
                        ></v-text-field>

                        <!-- Emoji-Picker-Button über gesteuerten v-menu -->
                        <v-menu
                          v-model="emojiMenu"
                          offset-y
                          :close-on-content-click="true"
                        >
                          <template v-slot:activator="{ props }">
                            <v-btn
                              icon
                              color="success"
                              v-bind="props"
                              class="ml-1"
                              @click.stop
                            >
                              <v-icon>mdi-emoticon</v-icon>
                            </v-btn>
                          </template>
                          <v-list class="emoji-list">
                            <v-list-item
                              v-for="emoji in emojiOptions"
                              :key="emoji"
                              @click.stop="selectEmoji(emoji)"
                              class="emoji-item"
                            >
                              <v-list-item-title>{{ emoji }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </div>
                  </div>

                  <v-divider></v-divider>

                  <v-list density="compact">
                    <v-list-item @click="forceRefresh">
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-refresh</v-icon>
                      </template>
                      <v-list-item-title>App aktualisieren</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="navigateTo('/qr-generator')">
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-qrcode</v-icon>
                      </template>
                      <v-list-item-title>QR-Code Generator</v-list-item-title>
                    </v-list-item>

                    <v-list-item @click="openUserPanelDialog">
                      <template v-slot:prepend>
                        <v-icon color="success">mdi-account-cog</v-icon>
                      </template>
                      <v-list-item-title>Mein Konto</v-list-item-title>
                    </v-list-item>

                    <v-divider></v-divider>

                    <v-list-item @click="logout">
                      <template v-slot:prepend>
                        <v-icon color="error">mdi-logout</v-icon>
                      </template>
                      <v-list-item-title>Abmelden</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card>
              </v-menu>
            </div>
          </v-app-bar>
        </template>

        <!-- Mobile-Navigation: Moderne Seitenleiste -->
        <template v-else>
          <v-app-bar
            app
            class="mobile-navbar"
            :class="{ 'navbar-hidden': !showAppBar }"
          >
            <v-app-bar-nav-icon
              @click="drawer = !drawer"
              class="menu-icon"
            ></v-app-bar-nav-icon>

            <div class="brand-container">
              <v-icon color="success" size="24">mdi-shield-account</v-icon>
              <v-toolbar-title class="app-title">ARCM</v-toolbar-title>
            </div>

            <v-spacer></v-spacer>

            <v-avatar
              size="32"
              color="success"
              class="user-avatar"
              @click="openUserPanelDialog"
            >
              <span v-if="authStore.user?.vorname && authStore.user?.nachname">
                {{
                  getInitials(authStore.user?.vorname, authStore.user?.nachname)
                }}
              </span>
              <v-icon v-else>mdi-account</v-icon>
            </v-avatar>
          </v-app-bar>
          <v-navigation-drawer
            v-model="drawer"
            app
            temporary
            class="mobile-drawer"
            width="280"
          >
            <div class="drawer-header">
              <div class="user-profile">
                <v-avatar size="64" color="success" class="drawer-avatar">
                  <span
                    v-if="authStore.user?.vorname && authStore.user?.nachname"
                  >
                    {{
                      getInitials(
                        authStore.user?.vorname,
                        authStore.user?.nachname
                      )
                    }}
                  </span>
                  <v-icon v-else size="40">mdi-account</v-icon>
                </v-avatar>
                <div class="user-info">
                  <div class="user-name">{{ authStore.user?.loginname }}</div>
                  <div class="user-role">
                    {{ getUserRole() }}
                  </div>
                </div>
              </div>

              <!-- Status-Indikator im mobilen Menü -->
              <div class="mobile-status-container mt-3 px-3">
                <UserStatusIndicator :showName="true" />
              </div>
            </div>

            <v-divider></v-divider>

            <v-list nav class="drawer-nav">
              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                prepend-icon="mdi-view-dashboard-outline"
                title="Dashboard"
                :active="isActive('/dashboard')"
                @click="navigateAndClose('/dashboard')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-view-dashboard-outline</v-icon>
                </template>
              </v-list-item>

              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer
                "
                :append-icon="
                  openMobileGroup === 'admin'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Adminbereich"
                @click="toggleMobileGroup('admin')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-shield-crown-outline</v-icon>
                </template>
              </v-list-item>

              <div v-show="openMobileGroup === 'admin'" class="submenu-items">
                <v-list-item
                  title="Admin-Bereich"
                  :active="isActive('/admin')"
                  class="submenu-item"
                  @click="navigateAndClose('/admin')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-shield-crown</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.super_admin || authStore.user?.developer"
                  title="Management"
                  :active="isActive('/management')"
                  class="submenu-item"
                  @click="navigateAndClose('/management')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-cogs</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.developer"
                  title="Dev Tools"
                  :active="isActive('/devtools')"
                  class="submenu-item"
                  @click="navigateAndClose('/devtools')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-alert</v-icon>
                  </template>
                </v-list-item>
              </div>

              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :append-icon="
                  openMobileGroup === 'communication'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Kommunikation"
                @click="toggleMobileGroup('communication')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-message-text-outline</v-icon>
                </template>
              </v-list-item>

              <div
                v-show="openMobileGroup === 'communication'"
                class="submenu-items"
              >
                <v-list-item
                  title="Kalender"
                  :active="isActive('/calendar')"
                  class="submenu-item"
                  @click="navigateAndClose('/calendar')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-calendar-month-outline</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  title="Chat"
                  :active="route.path.includes('/chat')"
                  class="submenu-item"
                  @click="navigateAndClose('/chat')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-chat</v-icon>
                  </template>
                  <v-badge
                    v-if="unreadChatMessages > 0"
                    :content="unreadChatMessages"
                    color="error"
                    dot
                    floating
                    class="ml-2"
                  ></v-badge>
                </v-list-item>

                <v-list-item
                  title="Nachrichten"
                  :active="isActive('/messages')"
                  class="submenu-item"
                  @click="navigateAndClose('/messages')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-email-outline</v-icon>
                  </template>
                  <v-badge
                    v-if="unreadMessages > 0"
                    :content="unreadMessages"
                    color="error"
                    inline
                    class="ml-2"
                  ></v-badge>
                </v-list-item>

                <v-list-item
                  title="Dynamische Listen"
                  :active="route.path.includes('/lists')"
                  class="submenu-item"
                  @click="navigateAndClose('/lists')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-format-list-bulleted</v-icon>
                  </template>
                </v-list-item>
              </div>

              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting ||
                  authStore.user?.dtc
                "
                :append-icon="
                  openMobileGroup === 'werkstatt'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Werkstatt"
                @click="toggleMobileGroup('werkstatt')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-tools</v-icon>
                </template>
              </v-list-item>

              <div v-show="openMobileGroup === 'werkstatt'" class="submenu-items">
                <v-list-item
                  v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                  title="Instandsetzung"
                  :active="isActive('/instandsetzung')"
                  class="submenu-item"
                  @click="navigateAndClose('/instandsetzung')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-wrench-outline</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                  title="Retouren"
                  :active="isActive('/retouren')"
                  class="submenu-item"
                  @click="navigateAndClose('/retouren')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-recycle</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="!isOnlyDtc()"
                  title="Retouren Sorting"
                  :active="isActive('/retouren-sorting')"
                  class="submenu-item"
                  @click="navigateAndClose('/retouren-sorting')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-package-variant-closed-remove</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user && (authStore.user.returen_sorting_v2 || authStore.user.admin || authStore.user.super_admin || authStore.user.developer)"
                  title="Retouren Sorting V2"
                  :active="isActive('/retouren-sorting-v2')"
                  class="submenu-item"
                  @click="navigateAndClose('/retouren-sorting-v2')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="primary">mdi-package-variant-closed</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user && (authStore.user.refurbish || authStore.user.admin || authStore.user.super_admin || authStore.user.developer)"
                  title="Refurbish Paletten"
                  :active="isActive('/refurbish')"
                  class="submenu-item"
                  @click="navigateAndClose('/refurbish')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="orange-darken-1">mdi-tools</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.dtc || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                  title="DTC Retouren"
                  :active="isActive('/dtc')"
                  class="submenu-item"
                  @click="navigateAndClose('/dtc')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="teal-darken-1">mdi-package-variant-closed</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                  title="B-Ware"
                  :active="isActive('/b-ware')"
                  class="submenu-item"
                  @click="navigateAndClose('/b-ware')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="amber-darken-2">mdi-quality-medium</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                  title="B-Ware Leistung"
                  :active="isActive('/b-ware-leistung')"
                  class="submenu-item"
                  @click="navigateAndClose('/b-ware-leistung')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="amber-darken-2">mdi-counter</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="!isOnlyDtc()"
                  title="Nebentätigkeiten"
                  :active="isActive('/nebentaetigkeiten')"
                  class="submenu-item"
                  @click="navigateAndClose('/nebentaetigkeiten')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-check-circle-outline</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="!isOnlyRetourenSorting() && !isOnlyDtc()"
                  title="Ersatzteile"
                  :active="isActive('/ersatzteile')"
                  class="submenu-item"
                  @click="navigateAndClose('/ersatzteile')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-barcode</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="!isOnlyDtc()"
                  title="IRIS Code Matrix"
                  :active="isActive('/iris-code-matrix')"
                  class="submenu-item"
                  @click="navigateAndClose('/iris-code-matrix')"
                >
                  <template v-slot:prepend>
                    <v-icon size="16" color="success">mdi-table-search</v-icon>
                  </template>
                </v-list-item>
              </div>

              <!-- Refurbish -->
              <v-list-item
                v-if="authStore.user?.refurbish && !authStore.user?.admin && !authStore.user?.super_admin && !authStore.user?.developer"
                title="Refurbish"
                :active="isActive('/refurbish')"
                class="menu-item"
                @click="navigateAndClose('/refurbish')"
              >
                <template v-slot:prepend>
                  <v-icon color="orange-darken-1">mdi-tools</v-icon>
                </template>
              </v-list-item>

              <!-- Office-Gruppe -->
              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.office
                "
                :append-icon="
                  openMobileGroup === 'office'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Office"
                @click="toggleMobileGroup('office')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="indigo-darken-1">mdi-briefcase</v-icon>
                </template>
              </v-list-item>

              <div v-show="openMobileGroup === 'office'" class="submenu-items">
                <v-list-item
                  title="Refurbish Freigabe"
                  :active="isActive('/office')"
                  class="submenu-item"
                  @click="navigateAndClose('/office')"
                >
                  <template v-slot:prepend>
                    <v-icon color="indigo-darken-1">mdi-package-check</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  title="KV-Bestätigungen"
                  :active="isActive('/kv-confirmations')"
                  class="submenu-item"
                  @click="navigateAndClose('/kv-confirmations')"
                >
                  <template v-slot:prepend>
                    <v-icon color="indigo-darken-1">mdi-file-document-check-outline</v-icon>
                  </template>
                </v-list-item>
              </div>

              <!-- Lager-Gruppe -->
              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.lagerist ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :append-icon="
                  openMobileGroup === 'storage'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Lager"
                @click="toggleMobileGroup('storage')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-package-variant</v-icon>
                </template>
              </v-list-item>

              <div v-show="openMobileGroup === 'storage'" class="submenu-items">
                <v-list-item
                  v-if="
                    authStore.user?.admin ||
                    authStore.user?.super_admin ||
                    authStore.user?.developer ||
                    authStore.user?.lagerist
                  "
                  title="Kommissionen"
                  :active="isActive('/commission')"
                  class="submenu-item"
                  @click="navigateAndClose('/commission')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-package-variant-closed</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.monteur || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer || authStore.user?.lagerist"
                  title="Anfrage"
                  :active="isActive('/request')"
                  class="submenu-item"
                  @click="navigateAndClose('/request')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-help-circle-outline</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  v-if="authStore.user?.lagerist || authStore.user?.admin || authStore.user?.super_admin || authStore.user?.developer"
                  title="Versand"
                  :active="isActive('/versand')"
                  class="submenu-item"
                  @click="navigateAndClose('/versand')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-truck-delivery</v-icon>
                  </template>
                </v-list-item>
              </div>

              <!-- Auswertung-Gruppe -->
              <v-list-item
                v-if="
                  authStore.user?.admin ||
                  authStore.user?.super_admin ||
                  authStore.user?.developer ||
                  authStore.user?.monteur ||
                  authStore.user?.returen_sorting
                "
                :append-icon="
                  openMobileGroup === 'reporting'
                    ? 'mdi-chevron-up'
                    : 'mdi-chevron-down'
                "
                title="Auswertung"
                @click="toggleMobileGroup('reporting')"
                class="menu-item"
              >
                <template v-slot:prepend>
                  <v-icon color="success">mdi-chart-box-outline</v-icon>
                </template>
              </v-list-item>

              <div v-show="openMobileGroup === 'reporting'" class="submenu-items">
                <v-list-item
                  title="Statistik"
                  :active="isActive('/statistik')"
                  class="submenu-item"
                  @click="navigateAndClose('/statistik')"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-chart-box-outline</v-icon>
                  </template>
                </v-list-item>

                <v-list-item
                  title="Zusammenfassungen"
                  class="submenu-item"
                  @click="promptPasswordAndClose"
                >
                  <template v-slot:prepend>
                    <v-icon color="success">mdi-text-box-outline</v-icon>
                  </template>
                </v-list-item>
              </div>
            </v-list>

            <template v-slot:append>
              <div class="drawer-footer">
                <v-btn
                  block
                  color="error"
                  prepend-icon="mdi-logout"
                  variant="tonal"
                  @click="logout"
                >
                  Abmelden
                </v-btn>
              </div>
            </template>
          </v-navigation-drawer>
        </template>

        <!-- Hauptinhalt mit angepasstem Abstand -->
        <v-main :class="isMobile ? 'mobile-main' : 'normal-main'">
          <v-container fluid class="main-container">
            <router-view></router-view>
          </v-container>
        </v-main>

        <!-- Rechte Sidebar für Kurztexte und Durchwahlen -->
        <RightSidebar
          v-if="authStore.isAuthenticated && route.path !== '/dashboard'"
        />

        <!-- Modernisierter Footer mit Glass-Effekt und schlagendem Herz -->
        <footer class="modern-footer">
          <div class="footer-content">
            <div class="footer-left">
              <div class="footer-logo">
                <v-icon color="success" size="20">mdi-shield-account</v-icon>
                <span class="footer-brand">ARCM</span>
              </div>
              <div class="footer-copyright">
                v{{ version }} Made with
                <v-icon
                  color="red"
                  size="small"
                  class="heartbeat-icon"
                  @click="goToServiceStatus"
                  >mdi-heart</v-icon
                >
                &copy; {{ new Date().getFullYear() }} alphadystopie.de · devhub-it.de
              </div>
            </div>

            <div class="footer-links">

              <router-link to="/changelogs" class="footer-link">
                <v-icon size="small" color="success">mdi-text-box-outline</v-icon>
                Changelogs
              </router-link>
              <div class="dot-divider"></div>
              <router-link to="/privacypolicy" class="footer-link">
                <v-icon size="small" color="success"
                  >mdi-shield-check-outline</v-icon
                >
                Datenschutz
              </router-link>
            </div>
          </div>
        </footer>

        <!-- Benutzerkontrollpanel-Dialog mit Scrolling -->
        <v-dialog
          v-model="userPanelDialog"
          max-width="700px"
          class="user-panel-dialog"
          scrollable
        >
          <v-card class="dialog-card">
            <v-card-title class="dialog-title position-relative">
              <div class="d-flex align-center">
                <v-icon color="success" class="mr-2">mdi-account-cog</v-icon>
                <span class="dialog-title-text">Benutzerkonto</span>
              </div>
              <v-btn
                variant="text"
                icon
                @click="userPanelDialog = false"
                class="dialog-close-btn"
              >
                <v-icon>mdi-close</v-icon>
              </v-btn>
            </v-card-title>
            <v-divider class="dialog-divider"></v-divider>
            <v-card-text class="dialog-content dialog-content-scrollable">
              <UserControlPanelView />
            </v-card-text>
          </v-card>
        </v-dialog>

        <!-- Passwort-Dialog -->
        <v-dialog
          v-model="passwordDialog"
          max-width="400"
          class="password-dialog"
        >
          <v-card class="dialog-card">
            <v-card-title class="dialog-title">
              <v-icon color="success" class="mr-2">mdi-lock-outline</v-icon>
              <span class="dialog-title-text">Passwort eingeben</span>
            </v-card-title>
            <v-divider class="dialog-divider"></v-divider>
            <v-card-text class="dialog-content pt-4">
              <v-text-field
                v-model="password"
                label="Passwort"
                type="password"
                variant="outlined"
                color="success"
                bg-color="rgba(30, 30, 40, 0.5)"
                prepend-inner-icon="mdi-key-outline"
                class="password-field"
                @keyup.enter="verifyPassword"
              ></v-text-field>
            </v-card-text>
            <v-divider class="dialog-divider"></v-divider>
            <v-card-actions class="dialog-actions">
              <v-spacer></v-spacer>
              <v-btn variant="text" color="grey" @click="passwordDialog = false">
                Abbrechen
              </v-btn>
              <v-btn
                variant="elevated"
                color="success"
                @click="verifyPassword"
                class="submit-btn"
              >
                <v-icon start>mdi-check</v-icon>
                Bestätigen
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Snackbar für globale Meldungen -->
        <v-snackbar
          v-model="snackbar.show"
          :timeout="3000"
          :color="snackbar.color"
          location="bottom"
          class="modern-snackbar"
        >
          {{ snackbar.message }}
        </v-snackbar>

        <!-- CookieConsent-Banner -->
        <CookieConsent />

        <!-- Loading Bar -->
        <v-progress-linear
          v-if="loading"
          indeterminate
          color="success"
          class="loading-bar"
        ></v-progress-linear>

        <!-- Announcement Toast System -->
        <transition-group
          name="announcement-transition"
          tag="div"
          class="announcement-container"
        >
          <v-card
            v-if="shouldShowAnnouncement && announcementText"
            :key="currentAnnouncementId"
            :class="[
              'announcement-toast',
              announcementType,
              { collapsed: isAnnouncementCollapsed },
            ]"
            elevation="8"
          >
            <div class="announcement-toast-content">
              <v-icon
                :color="announcementType === 'warning' ? 'error' : 'success'"
                size="24"
                class="announcement-icon"
              >
                {{
                  announcementType === "warning"
                    ? "mdi-alert-circle"
                    : "mdi-bell-ring"
                }}
              </v-icon>

              <!-- Content basierend auf Collapsed-Status -->
              <div class="announcement-text-container">
                <div class="announcement-title">
                  {{
                    announcementType === "warning"
                      ? "Wichtige Warnung!"
                      : "Ankündigung"
                  }}
                </div>
                <div v-if="!isAnnouncementCollapsed" class="announcement-message">
                  {{ announcementText }}
                </div>
              </div>

              <!-- Toggle-Button zum Ein-/Ausklappen -->
              <v-btn
                icon
                variant="text"
                size="small"
                class="collapse-toggle"
                @click.stop="toggleAnnouncementCollapse"
              >
                <v-icon>{{
                  isAnnouncementCollapsed ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </div>

            <v-card-actions
              v-if="!isAnnouncementCollapsed"
              class="announcement-actions"
            >
              <v-spacer></v-spacer>
              <v-btn
                variant="text"
                size="small"
                color="default"
                @click="dismissAnnouncement"
                class="dismiss-btn"
              >
                Schließen
              </v-btn>
              <v-btn
                v-if="announcementType === 'warning'"
                variant="elevated"
                color="error"
                size="small"
                @click="dismissAnnouncement"
                class="warning-btn"
              >
                Verstanden
              </v-btn>
            </v-card-actions>
          </v-card>
        </transition-group>

        <!-- KV-Bestätigungen Persistent Popup -->
        <transition-group
          name="kv-notification-transition"
          tag="div"
          class="kv-notification-container"
        >
          <v-card
            v-if="shouldShowKvNotification && newKvCount > 0"
            :key="`kv-notification-${newKvCount}`"
            :class="[
              'kv-notification-popup',
              { collapsed: isKvNotificationCollapsed },
            ]"
            elevation="8"
          >
            <div class="kv-notification-content">
              <v-icon
                color="primary"
                size="24"
                class="kv-notification-icon"
              >
                mdi-clipboard-check
              </v-icon>

              <!-- Content basierend auf Collapsed-Status -->
              <div class="kv-notification-text-container">
                <div class="kv-notification-title">
                  Neue KV-Bestätigung{{ newKvCount > 1 ? 'en' : '' }}
                </div>
                <div v-if="!isKvNotificationCollapsed" class="kv-notification-message">
                  Sie haben {{ newKvCount }} neue KV-Bestätigung{{ newKvCount > 1 ? 'en' : '' }} erhalten.
                </div>
              </div>

              <!-- Toggle-Button zum Ein-/Ausklappen -->
              <v-btn
                icon
                variant="text"
                size="small"
                class="collapse-toggle"
                @click.stop="toggleKvNotificationCollapse"
              >
                <v-icon>{{
                  isKvNotificationCollapsed ? "mdi-chevron-up" : "mdi-chevron-down"
                }}</v-icon>
              </v-btn>
            </div>

            <v-card-actions
              v-if="!isKvNotificationCollapsed"
              class="kv-notification-actions"
            >
              <v-spacer></v-spacer>
              <v-btn
                variant="text"
                size="small"
                color="default"
                @click="dismissKvNotification"
                class="dismiss-btn"
              >
                Später
              </v-btn>
              <v-btn
                variant="elevated"
                color="primary"
                size="small"
                @click="goToKvMessages"
                class="action-btn"
              >
                Ansehen
              </v-btn>
            </v-card-actions>
          </v-card>
        </transition-group>
      </template>
    </template>
  </v-app>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  provide,
  watch,
  nextTick,
} from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import { useToast } from "vue-toastification";
import UserControlPanelView from "@/views/UserControlPanelView.vue";
import axios from "axios";
import { API_BASE_URL, WS_URL } from "@/config";
import CookieConsent from "@/components/CookieConsent.vue";
import UserStatusIndicator from "@/components/UserStatusIndicator.vue";
import { setupAuthInterceptor } from "@/utils/axiosSetup";
import VersionService from "@/services/VersionService";
import ChatNotificationService from "@/services/ChatNotificationService";
import CommissionNotificationService from "@/services/CommissionNotificationService";
import WebSocketService from "@/services/WebSocketService";
import RightSidebar from "@/components/RightSidebar.vue";
import DesktopView from '@/components/DesktopView.vue';

const toast = useToast();
const isAuthLoading = ref(true);
const version = ref(VersionService.getVersion());
const unreadChatMessages = computed(() => {
  // ChatNotificationService auf vorhandene Benachrichtigungen prüfen
  return ChatNotificationService.unreadMessages.value || 0;
});

// Message Center - Ungelesene Nachrichten Counter
const unreadMessages = ref(0);

// Variable für das derzeit geöffnete Desktop-Dropdown-Menü
const openDesktopMenu = ref(null);

// Funktion zum Steuern der Desktop-Dropdown-Menüs
const handleDesktopMenu = (menuName, isOpen) => {
  // Wenn ein Menü geöffnet wird, setze es als aktives Menü
  if (isOpen) {
    openDesktopMenu.value = menuName;
  }
  // Wenn ein Menü geschlossen wird und es ist das aktive Menü,
  // setze das aktive Menü zurück
  else if (openDesktopMenu.value === menuName) {
    openDesktopMenu.value = null;
  }
};

// Füge diese Variablen hinzu
const currentAppVersion = ref(VersionService.getVersion());
const updateAvailable = ref(false);
const updateCheckInterval = ref(null);

const forceRefresh = () => {
  // Cache löschen und neu laden
  if ("caches" in window) {
    caches.keys().then((keys) => {
      keys.forEach((key) => caches.delete(key));
    });
  }
  // Hard-Reload mit Cache-Busting
  window.location.href = window.location.pathname + "?refresh=" + Date.now();
};

// Neue Funktion zur Versionsprüfung
const checkForUpdates = async (forcedCheck = false) => {
  try {

    // Cache-Umgehungs-Parameter hinzufügen
    const cacheBuster = `nocache=${Date.now()}`;
    const response = await axios.get(
      `${API_BASE_URL}version.php?action=get&${cacheBuster}`,
      {
        withCredentials: true,
      }
    );

    if (response.data && response.data.status === "success") {
      const serverVersion = response.data.data.version;

      // WICHTIGE ÄNDERUNG: Nur aktualisieren, wenn Server-Version höher ist
      if (
        VersionService.isNewerVersion(serverVersion, currentAppVersion.value)
      ) {
        updateAvailable.value = true;

        // Sofort einen Update ausführen, wenn Version unterschiedlich ist
        showUpdateNotification(serverVersion);
        return true;
      } else {
      }
    }
    return false;
  } catch (error) {
    console.error("Fehler bei der Update-Prüfung:", error);
    return false;
  }
};

// Benachrichtigung über neues Update anzeigen
const showUpdateNotification = async (newVersion) => {

  // Alle Caches löschen
  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map(key => caches.delete(key)));
  }

  // Service Worker deregistrieren und neu registrieren
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (let registration of registrations) {
      await registration.unregister();
    }
  }

  // Kurze Benachrichtigung anzeigen
  announcementText.value = `Neue Version verfügbar (${newVersion}). Cache wird geleert...`;
  announcementType.value = "announcement";
  shouldShowAnnouncement.value = true;

  // Nach 1 Sekunde hart neuladen mit mehreren Cache-Busting-Parametern
  setTimeout(() => {
    const url = new URL(window.location.href);
    url.searchParams.set('v', newVersion);
    url.searchParams.set('_t', Date.now());
    url.searchParams.set('nocache', '1');

    // Hard Reload erzwingen
    window.location.replace(url.href);
  }, 1000);
};

// Inaktivitäts-Timer einrichten - einmal pro Minute die inactive_minutes erhöhen
let inactivityUpdateInterval = null;

const setupInactivityTimer = () => {
  if (inactivityUpdateInterval) clearInterval(inactivityUpdateInterval);

  // Nur starten, wenn der Benutzer angemeldet ist
  if (!authStore.isAuthenticated) return;

  inactivityUpdateInterval = setInterval(async () => {
    // Nur ausführen, wenn die Seite sichtbar ist
    if (document.visibilityState === "visible") {
      try {
        await axios.get(`${API_BASE_URL}updateInactivity.php?action=update`, {
          withCredentials: true,
        });
      } catch (error) {
        console.error("Fehler beim Aktualisieren der Inaktivitätszeit:", error);
      }
    }
  }, 60000); // Alle 60 Sekunden (1 Minute)
};

// Neue Hilfsfunktionen zur Überprüfung, ob sich der Benutzer in einer bestimmten Sektion befindet
const isInCommunicationSection = () => {
  const path = router.currentRoute.value.path;
  return ["/calendar", "/chat", "/messages", "/lists"].some((route) => path.includes(route));
};

const isInOfficeSection = () => {
  const path = router.currentRoute.value.path;
  return ["/office", "/kv-confirmations", "/inventur/auswertung"].includes(path);
};

const isInStorageSection = () => {
  const path = router.currentRoute.value.path;
  return ["/commission", "/request", "/versand", "/inventur"].includes(path);
};

const isInReportingSection = () => {
  const path = router.currentRoute.value.path;
  return ["/statistik", "/summary"].includes(path);
};

// Authentifizierungs-Check-Intervall (alle 60 Sekunden)
let authCheckInterval = null;

// Periodische Überprüfung der Session-Gültigkeit
const setupAuthCheck = () => {
  if (authCheckInterval) clearInterval(authCheckInterval);

  authCheckInterval = setInterval(async () => {
    // Nur überprüfen, wenn der Benutzer angemeldet ist und nicht auf der Login-Seite
    if (authStore.isAuthenticated && !isLoginPage.value) {
      try {
        // Hintergrund-Tabs: Session nur validieren, last_activity NICHT anfassen
        // (sonst bleiben „Geister“ in Aktive Benutzer sichtbar)
        const touchActivity = document.visibilityState === "visible";
        await axios.post(
          `${API_BASE_URL}auth_check.php`,
          { action: "check", touch_activity: touchActivity },
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );
        // Wenn der Aufruf erfolgreich ist, ist die Session noch gültig
      } catch (error) {
        // Fehler bedeutet ungültige Session -> abmelden und zur Login-Seite weiterleiten
        if (error.response && error.response.status === 401) {
          console.warn("Sitzung ungültig oder beendet. Sie werden abgemeldet.");
          await authStore.resetSession();
          router.push("/");
        }
      }
    }
  }, 60000); // 60 Sekunden
};

// In den script-Bereich nach der isInWerkstattSection-Funktion einfügen
const isInAdminSection = () => {
  const path = router.currentRoute.value.path;
  return ["/admin", "/management"].includes(path);
};

const router = useRouter();
const authStore = useAuthStore();
const route = useRoute();
const openMobileGroup = ref(null);
const toggleMobileGroup = (group) => {
  // Wenn das gleiche Menü geklickt wird, umschalten
  if (openMobileGroup.value === group) {
    openMobileGroup.value = null;
  } else {
    // Wenn ein anderes Menü geklickt wird, dieses öffnen und andere schließen
    openMobileGroup.value = group;
  }
};

// Announcement-Handling
const announcementText = ref("");
const announcementType = ref("announcement");
const currentAnnouncementId = ref("");
const isAnnouncementCollapsed = ref(false);
const shouldShowAnnouncement = ref(false);

// Announcement ein-/ausklappen
const toggleAnnouncementCollapse = () => {
  isAnnouncementCollapsed.value = !isAnnouncementCollapsed.value;
  // Optional: Speichern des Zustands im localStorage
  localStorage.setItem(
    `announcement-collapsed-${currentAnnouncementId.value}`,
    isAnnouncementCollapsed.value
  );
};

// KV-Benachrichtigungs-Handling
const newKvCount = ref(0);
const isKvNotificationCollapsed = ref(false);
const shouldShowKvNotification = ref(false);
const seenKvIds = ref(new Set());

// KV-Benachrichtigung ein-/ausklappen
const toggleKvNotificationCollapse = () => {
  isKvNotificationCollapsed.value = !isKvNotificationCollapsed.value;
  localStorage.setItem(
    'kv-notification-collapsed',
    isKvNotificationCollapsed.value
  );
};

// KV-Benachrichtigung schließen
const dismissKvNotification = () => {
  shouldShowKvNotification.value = false;

  // Mit Animation ausblenden
  setTimeout(() => {
    newKvCount.value = 0;
  }, 400);
};

// Zu Nachrichten/Message Center navigieren
const goToKvMessages = () => {
  dismissKvNotification();
  router.push('/messages');
};

// UI-Status-Variablen
const drawer = ref(false);
const scrolled = ref(false);
const snackbar = ref({
  show: false,
  message: "",
  color: "success",
});
const userPanelDialog = ref(false);
const passwordDialog = ref(false);
const password = ref("");
const loading = ref(false); // Loading state

// Fenstergrößen und responsive Anpassungen
const windowWidth = ref(window.innerWidth);
const isMobile = computed(() => windowWidth.value < 960);

// Prüfe, ob die aktuelle Route die Loginseite ist
const isLoginPage = computed(() => route.path === "/");

// Dynamische Begrüßung basierend auf Tageszeit
const greeting = computed(() => {
  const hour = new Date().getHours();
  if (hour < 12) return "Guten Morgen";
  if (hour < 18) return "Guten Tag";
  return "Guten Abend";
});

const handleStatusClick = async (status, event) => {
  if (status === "custom") {
    // Verhindert, dass das Menü schließt
    event.stopPropagation();
    // Setze den Status custom – das lässt das Eingabefeld erscheinen
    await authStore.updateUserStatus("custom");
    customStatusMessage.value = authStore.currentStatusMessage || "";
  } else {
    await setUserStatus(status);
  }
};

// Announcements empfangen und anzeigen - nutzt zentrale WebSocket-Verbindung
const setupAnnouncementSocket = () => {
  // Handler für Announcements registrieren
  WebSocketService.addMessageHandler('announcement', handleAnnouncementMessage);
  WebSocketService.addMessageHandler('warning', handleAnnouncementMessage);
  WebSocketService.addMessageHandler('announcement_cleared', handleAnnouncementCleared);
};

// Handler für Announcement-Nachrichten
const handleAnnouncementMessage = (data) => {
  announcementText.value = data.message || "";
  announcementType.value = data.type;
  currentAnnouncementId.value = data.id || Date.now().toString();
  isAnnouncementCollapsed.value = false;
  shouldShowAnnouncement.value = true;

  // Optional: Sound abspielen
  playNotificationSound();
};

// Handler für das Löschen von Announcements
const handleAnnouncementCleared = () => {
  shouldShowAnnouncement.value = false;
  setTimeout(() => {
    announcementText.value = "";
    currentAnnouncementId.value = "";
  }, 400);
};

// Funktion zum Aufräumen der Handler
const cleanupAnnouncementHandlers = () => {
  WebSocketService.removeMessageHandler('announcement', handleAnnouncementMessage);
  WebSocketService.removeMessageHandler('warning', handleAnnouncementMessage);
  WebSocketService.removeMessageHandler('announcement_cleared', handleAnnouncementCleared);
};

// Announcement schließen
const dismissAnnouncement = () => {
  if (currentAnnouncementId.value) {
    localStorage.setItem("dismissedAnnouncement", currentAnnouncementId.value);
  }
  shouldShowAnnouncement.value = false;

  // Mit Animation ausblenden
  setTimeout(() => {
    announcementText.value = "";
  }, 400); // Nach Ende der Ausblend-Animation
};

// Optional: Benachrichtigungssound
const playNotificationSound = () => {
  const audio = new Audio("/sounds/notification-sound.mp3");
  audio.volume = 0.5;
  audio
    .play()
    .catch(() => {});
};

// Navigation und Routing
const isActive = (route) => {
  return router.currentRoute.value.path === route;
};

const isInWerkstattSection = () => {
  const path = router.currentRoute.value.path;
  return ["/instandsetzung", "/retouren", "/nebentaetigkeiten", "/dtc", "/retouren-sorting", "/retouren-sorting-v2", "/refurbish", "/ersatzteile", "/iris-code-matrix", "/b-ware", "/b-ware-leistung"].includes(path);
};

const navigateTo = (route) => {
  if (router.currentRoute.value.path !== route) {
    router.push(route);
  }
};

const navigateAndClose = (route) => {
  navigateTo(route);
  drawer.value = false;
};

// Benutzerinteraktionen
const logout = async () => {
  // Handler aufräumen
  cleanupAnnouncementHandlers();
  await authStore.logout();
  router.replace("/");
};

const openUserPanelDialog = () => {
  userPanelDialog.value = true;
};

const promptPassword = () => {
  passwordDialog.value = true;
};

const promptPasswordAndClose = () => {
  passwordDialog.value = true;
  drawer.value = false;
};

const verifyPassword = async () => {
  try {
    const response = await axios.post(
      `${API_BASE_URL}verify_password.php`,
      { action: "verify", password: password.value },
      { withCredentials: true }
    );

    if (response.data.status === "success") {
      navigateTo("/summary");
      passwordDialog.value = false;
      password.value = ""; // Passwort zurücksetzen
    } else {
      showToast("Falsches Passwort", "error");
    }
  } catch (error) {
    showToast("Serverfehler! Bitte versuche es später erneut.", "error");
  }
};

// Hilfsfunktionen
const statusOptions = [
  {
    label: "Online",
    value: "online",
    icon: "mdi-circle",
    description: "Verfügbar für Kontakte",
  },
  {
    label: "Abwesend",
    value: "away",
    icon: "mdi-clock-outline",
    description: "Kurzzeitig nicht am Platz",
  },
  {
    label: "Beschäftigt",
    value: "busy",
    icon: "mdi-minus-circle",
    description: "Bitte nicht stören",
  },
  {
    label: "Mobil",
    value: "mobile",
    icon: "mdi-cellphone",
    description: "Unterwegs erreichbar",
  },
  {
    label: "Benutzerdefiniert",
    value: "custom",
    icon: "mdi-pencil",
    description: "Eigene Statusmeldung",
  },
  {
    label: "Offline",
    value: "offline",
    icon: "mdi-circle-outline",
    description: "Als offline anzeigen",
  },
];

const customStatusMessage = ref(authStore.currentStatusMessage || "");

const getStatusColor = (status) => {
  switch (status) {
    case "online":
      return "success";
    case "away":
      return "amber";
    case "busy":
      return "error";
    case "custom":
      return "info";
    case "mobile":
      return "purple";
    case "offline":
    default:
      return "grey";
  }
};

const getStatusText = (status) => {
  if (status === "custom" && authStore.currentStatusMessage) {
    return authStore.currentStatusMessage;
  }

  switch (status) {
    case "online":
      return "Online";
    case "away":
      return "Abwesend";
    case "busy":
      return "Beschäftigt";
    case "mobile":
      return "Mobil";
    case "custom":
      return "Benutzerdefiniert";
    case "offline":
    default:
      return "Offline";
  }
};

const fetchCompleteUserData = async () => {
  if (!authStore.user || !authStore.isAuthenticated) return;

  try {
    const response = await axios.get(`${API_BASE_URL}activeUsers.php?action=get_active_users`, {
      withCredentials: true,
    });

    if (
      response.data.status === "success" &&
      Array.isArray(response.data.users)
    ) {
      // Den eigenen Benutzer anhand der ID finden
      const currentUser = response.data.users.find(
        (u) => u.id === authStore.user.id
      );

      if (currentUser) {
        // Aktualisiere die Benutzerdaten mit den vollständigen Daten vom Server
        authStore.user = {
          ...authStore.user,
          ...currentUser,
          // Status explizit setzen, falls er fehlt
          user_status:
            currentUser.user_status || authStore.user.user_status || "online",
        };

        // Im localStorage aktualisieren
        localStorage.setItem("userData", JSON.stringify(authStore.user));

        // Zusätzlich: WebSocket-Update senden
        authStore.sendStatusUpdateWebSocket(authStore.user.user_status);
      }
    }
  } catch (error) {
    console.error(
      "Fehler beim Abrufen der vollständigen Benutzerdaten:",
      error
    );
  }
};

const emojiOptions = [
  "😀",
  "😃",
  "😄",
  "😁",
  "😆",
  "😉",
  "😊",
  "😋",
  "😎",
  "😍",
  "😘",
  "🥰",
];

const emojiMenu = ref(false); // Steuert den Öffnungszustand des Emoji-Pickers

const insertEmoji = (emoji) => {
  // Emoji an das Ende des aktuellen Textes anhängen
  customStatusMessage.value += emoji;
};

const selectEmoji = (emoji) => {
  insertEmoji(emoji);
  // Schließe den Emoji-Picker nach Auswahl
  emojiMenu.value = false;
};

const setUserStatus = async (status) => {
  // Grundsätzlich den Status im Store aktualisieren
  await authStore.updateUserStatus(status);

  // Für benutzerdefinierten Status zusätzlich die Nachricht vorbereiten
  if (status === "custom") {
    customStatusMessage.value = authStore.currentStatusMessage || "";
  }
};

const updateCustomStatus = async () => {
  if (customStatusMessage.value.trim()) {
    await authStore.updateUserStatus("custom", customStatusMessage.value);
  }
};

const getInitials = (firstName, lastName) => {
  if (!firstName && !lastName) return "?";
  return `${firstName ? firstName.charAt(0).toUpperCase() : ""}${
    lastName ? lastName.charAt(0).toUpperCase() : ""
  }`;
};

const getUserRole = () => {
  if (!authStore.user) return "";
  if (authStore.user.super_admin) return "Super Administrator";
  if (authStore.user.admin) return "Administrator";
  if (authStore.user.developer) return "Developer";
  if (authStore.user.monteur) return "Monteur";
  if (authStore.user.returen_sorting) return "Retouren Sorting";
  if (authStore.user.lagerist) return "Lagerist";
  return "Benutzer";
};

const showToast = (message, color = "success") => {
  snackbar.value.message = message;
  snackbar.value.color = color;
  snackbar.value.show = true;
};

provide("showToast", showToast);

// Scroll-Handling für Ein-/Ausblenden der Navigationleiste
const showAppBar = ref(true);
let lastScrollY = window.pageYOffset || document.documentElement.scrollTop;

const handleScroll = () => {
  const currentScrollY =
    window.pageYOffset || document.documentElement.scrollTop;

  // Scroll-Status für visuelle Effekte
  scrolled.value = currentScrollY > 20;

  // Auto-Hide Navigation beim Herunterscrollen (außer auf mobilen Geräten)
  if (!isMobile.value) {
    // Wenn der Drawer geöffnet ist, immer App-Bar anzeigen
    if (drawer.value) {
      showAppBar.value = true;
      lastScrollY = currentScrollY;
      return;
    }

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      showAppBar.value = false;
    } else {
      showAppBar.value = true;
    }
    lastScrollY = currentScrollY;
  }
};

// Flag um sicherzustellen, dass WebSocket-Handler nur einmal registriert werden
let websocketHandlersRegistered = false;

const initWebSocket = () => {
  if (authStore.isAuthenticated) {
    // Auth laeuft ueber HttpOnly-Cookie am Same-Origin-/ws-Proxy
    WebSocketService.connect();

    // WebSocket-Handler nur EINMAL registrieren
    if (!websocketHandlersRegistered) {
      websocketHandlersRegistered = true;

      // Zuerst alle bestehenden Handler entfernen (verhindert Duplikate bei Hot-Reload)
      WebSocketService.clearMessageHandlers("update_notification");
      WebSocketService.clearMessageHandlers("new_message");
      WebSocketService.clearMessageHandlers("new_kv_confirmation");
      WebSocketService.clearMessageHandlers("kv_completed");
      WebSocketService.clearMessageHandlers("kv_archived");
      WebSocketService.clearMessageHandlers("kv_deleted");
      WebSocketService.clearMessageHandlers("kv_takeover");
      WebSocketService.clearMessageHandlers("new_commission_request");

      // WebSocket-Nachrichtenhandler für Updates hinzufügen
      WebSocketService.addMessageHandler("update_notification", (data) => {
        if (data.version && data.version !== currentAppVersion.value) {
          showUpdateNotification(data.version);
        }
      });

      // WebSocket-Handler für neue Nachrichten
      WebSocketService.addMessageHandler("new_message", (data) => {
        // Aktualisiere den Counter für ungelesene Nachrichten
        loadUnreadMessagesCount();

        // Toast zentral in App.vue anzeigen (immer gemountet)
        if (data.sender_name) {
          toast.info(`Neue Nachricht von ${data.sender_name}`);
        }

        // Dispatche Event für MessageCenterView (für Listen-Update)
        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      // WebSocket-Handler für neue KV-Bestätigungen
      WebSocketService.addMessageHandler("new_kv_confirmation", (data) => {
        // Hier könnte man ebenfalls den Counter aktualisieren, falls gewünscht
        loadUnreadMessagesCount();

        // Toast zentral in App.vue anzeigen
        if (data.sender_name) {
          toast.info(`Neue KV-Bestätigung: ${data.kv_nummer}`);
        }

        // Für alle Monteure: Persistentes Popup anzeigen (unabhängig von anderen Rollen)
        if (authStore.user?.monteur) {
          // Prüfen ob diese KV bereits gesehen wurde
          const kvId = data.kv_id || data.id;
          if (kvId && !seenKvIds.value.has(kvId)) {
            seenKvIds.value.add(kvId);
            newKvCount.value++;
            shouldShowKvNotification.value = true;
            isKvNotificationCollapsed.value = false;

            // Im localStorage speichern
            const seenKvArray = Array.from(seenKvIds.value);
            localStorage.setItem('seen-kv-ids', JSON.stringify(seenKvArray));
          }
        }

        // Dispatche Event für MessageCenterView und KvConfirmationView (für Listen-Update)
        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      // WebSocket-Handler für abgeschlossene KV-Bestätigungen
      WebSocketService.addMessageHandler("kv_completed", (data) => {
        loadUnreadMessagesCount();

        // Toast zentral in App.vue anzeigen
        if (data.monteur_name) {
          toast.success(`KV ${data.kv_nummer} wurde abgeschlossen`);
        }

        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      // WebSocket-Handler für archivierte KV-Bestätigungen
      WebSocketService.addMessageHandler("kv_archived", (data) => {
        // Toast zentral in App.vue anzeigen
        if (data.archiver_name) {
          toast.info(`KV ${data.kv_nummer} wurde archiviert`);
        }

        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      // WebSocket-Handler für gelöschte KV-Bestätigungen
      WebSocketService.addMessageHandler("kv_deleted", (data) => {
        loadUnreadMessagesCount();

        // Toast zentral in App.vue anzeigen
        toast.warning(`KV ${data.kv_nummer} wurde gelöscht`);

        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      // WebSocket-Handler für übernommene KV-Bestätigungen
      WebSocketService.addMessageHandler("kv_takeover", (data) => {
        loadUnreadMessagesCount();

        // Toast für alten Monteur anzeigen (nur wenn ich der alte Monteur bin)
        if (authStore.user?.id && data.userId === authStore.user.id) {
          toast.info(`KV ${data.kv_nummer} wurde von ${data.new_monteur} übernommen`);
        }

        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });

      WebSocketService.addMessageHandler("new_commission_request", (data) => {
        const isCommissionUser =
          authStore.user?.lagerist ||
          authStore.user?.admin ||
          authStore.user?.super_admin ||
          authStore.user?.developer;

        if (!isCommissionUser) {
          return;
        }

        CommissionNotificationService.notifyNewRequest(data, {
          suppressBrowser: route.path === '/commission',
          onNavigate: () => router.push('/commission'),
        });

        window.dispatchEvent(new CustomEvent('websocket-message', { detail: data }));
      });
    }
  }
};

// Funktion zum Laden ungelesener KV-Bestätigungen für Monteure
const loadUnreadKvConfirmations = async () => {

  if (!authStore.isAuthenticated) {
    return;
  }

  // Nur für Monteure (unabhängig von anderen Rollen)
  if (!authStore.user?.monteur) {
    return;
  }

  try {
    const response = await axios.get(`${API_BASE_URL}kv_confirmations.php`, {
      params: {
        action: 'list'
      },
      withCredentials: true
    });

    if (response.data.status === 'success' && response.data.data) {
      // Lade bereits gesehene KV-IDs aus localStorage
      const seenKvArray = JSON.parse(localStorage.getItem('seen-kv-ids') || '[]');
      seenKvIds.value = new Set(seenKvArray);

      // Zähle neue (noch nicht gesehene) KVs, die noch nicht abgeschlossen wurden
      const unseenKvs = response.data.data.filter(kv => {
        const isNotCompleted = !kv.is_completed || kv.is_completed === '0' || kv.is_completed === 0;
        const isUnseen = !seenKvIds.value.has(kv.id);
        return isUnseen && isNotCompleted;
      });

      if (unseenKvs.length > 0) {
        newKvCount.value = unseenKvs.length;
        shouldShowKvNotification.value = true;

        // Lade collapsed-Status aus localStorage
        const collapsedState = localStorage.getItem('kv-notification-collapsed');
        isKvNotificationCollapsed.value = collapsedState === 'true';
      }
    }
  } catch (error) {
    console.error('❌ Fehler beim Laden der KV-Bestätigungen:', error);
  }
};

// Funktion zum Laden der Anzahl ungelesener Nachrichten
const loadUnreadMessagesCount = async () => {
  if (!authStore.isAuthenticated) return;

  try {
    const response = await axios.get(`${API_BASE_URL}messages.php`, {
      params: { action: 'unread_count' },
      withCredentials: true
    });

    if (response.data.status === 'success') {
      unreadMessages.value = response.data.count || 0;
    }
  } catch (error) {
    console.error('Fehler beim Laden der ungelesenen Nachrichten:', error);
  }
};

// Event-Listener für Fenstergröße und Scrollposition
const resizeHandler = () => {
  windowWidth.value = window.innerWidth;
};

// Komponenten-Lifecycle
onMounted(async () => {
  // Setup Auth-Interceptor für globale Fehlererkennung
  await checkForUpdates(true);
  isAuthLoading.value = true;
  try {
    // Nur einen API-Aufruf machen, wenn wir bereits lokale Daten haben
    if (authStore.isAuthenticated) {
      // Authentifizierung explizit überprüfen und aktualisieren
      await authStore.checkAuth();
    } else if (localStorage.getItem("userData")) {
      // Falls wir Daten im localStorage haben, aber noch nicht im Store
      await authStore.restoreAuthentication();
    }

    // WebSocket-Verbindung erst herstellen, nachdem die Authentifizierung abgeschlossen ist
    if (authStore.isAuthenticated) {
      WebSocketService.connect();
      fetchCompleteUserData();
      // Activity Tracking wird bereits in checkAuth() gestartet, nicht nochmal hier!
    }
  } catch (error) {
    console.error("Fehler beim Initialisieren der App:", error);
  } finally {
    // Ladevorgang abschließen, unabhängig vom Ergebnis
    isAuthLoading.value = false;
  }

  setupAuthInterceptor();
  setupInactivityTimer();
  updateCheckInterval.value = setInterval(checkForUpdates, 5 * 60 * 1000); // Alle 5 Minuten

  // Starte Auth-Check, wenn Benutzer angemeldet ist
  if (authStore.isAuthenticated) {
    setupAuthCheck();
    fetchCompleteUserData();
    // Hier die WebSocket-Verbindung initialisieren
    initWebSocket();
    // Lade Anzahl ungelesener Nachrichten
    loadUnreadMessagesCount();
    // Lade ungelesene KV-Bestätigungen für Monteure
    loadUnreadKvConfirmations();
  }

  if (!authStore.user && authStore.isAuthenticated) {
    await authStore.checkAuth();
  }

  window.addEventListener("resize", resizeHandler);
  window.addEventListener("scroll", handleScroll);

  // WebSocket für Announcements starten
  if (!isLoginPage.value) {
    setupAnnouncementSocket();
  }

  // Event-Listener für Sichtbarkeitsänderungen hinzufügen
  document.addEventListener('visibilitychange', handleVisibilityChange);

  // Event-Listener für manuelle unread messages updates
  window.addEventListener('update-unread-messages', loadUnreadMessagesCount);

  // Event-Listener für KV-Bestätigungen als gelesen markieren
  window.addEventListener('mark-kv-as-seen', (event) => {
    const kvId = event.detail?.kvId;
    if (kvId) {
      seenKvIds.value.add(kvId);
      localStorage.setItem('seen-kv-ids', JSON.stringify(Array.from(seenKvIds.value)));

      // Counter aktualisieren
      if (newKvCount.value > 0) {
        newKvCount.value--;
      }

      // Popup ausblenden wenn keine neuen KVs mehr
      if (newKvCount.value === 0) {
        shouldShowKvNotification.value = false;
      }
    }
  });

  // Bestehende Event-Listener...
  window.addEventListener("resize", () => {
    resizeHandler();
  });
});

// Neue Funktion zum Behandeln von Sichtbarkeitsänderungen
const handleVisibilityChange = async () => {
  if (document.visibilityState === 'visible') {
    // Wenn die Seite wieder sichtbar wird (z.B. nach Browser-Neustart)
    if (authStore.isAuthenticated) {
      try {
        // Authentifizierung überprüfen
        await authStore.checkAuth();

        // WebSocket-Verbindungen neu aufbauen
        WebSocketService.connect();
        if (!isLoginPage.value) {
          setupAnnouncementSocket();
        }

        // Benutzerdaten aktualisieren
        fetchCompleteUserData();
      } catch (error) {
        console.error('Fehler beim Wiederherstellen der Verbindungen:', error);
      }
    }
  }
};

// Auf Änderungen im Auth-Status reagieren
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      setupAuthCheck();
      fetchCompleteUserData();
      initWebSocket(); // WebSocket starten
    } else {
      if (authCheckInterval) {
        clearInterval(authCheckInterval);
        authCheckInterval = null;
      }
      WebSocketService.disconnect(); // WebSocket trennen
    }
  }
);

// Route-Watcher für Titel-Management
watch(
  () => route.path,
  (newPath) => {
    // Dynamische Titel basierend auf der Route
    const titleMap = {
      '/': 'ARCM Core - Login',
      '/dashboard': 'ARCM Core - Dashboard',
      '/admin': 'ARCM Core - Admin',
      '/management': 'ARCM Core - Management',
      '/devtools': 'ARCM Core - Dev Tools',
      '/reinigungsplan': 'ARCM Core - Reinigungsplan',
      '/calendar': 'ARCM Core - Kalender',
      '/chat': 'ARCM Core - Chat',
      '/instandsetzung': 'ARCM Core - Instandsetzung',
      '/retouren': 'ARCM Core - Retouren',
      '/b-ware': 'ARCM Core - B-Ware',
      '/b-ware-leistung': 'ARCM Core - B-Ware Leistung',
      '/nebentaetigkeiten': 'ARCM Core - Nebentätigkeiten',
      '/summary': 'ARCM Core - Zusammenfassung',
      '/statistik': 'ARCM Core - Statistik',
      '/spare-parts': 'ARCM Core - Ersatzteile',
      '/settings': 'ARCM Core - Einstellungen',
      '/impressum': 'ARCM Core - Impressum'
    };

    // Setze den Titel basierend auf der Route
    const defaultTitle = titleMap[newPath] || 'ARCM Core';

    // Sofortiges Setzen des Titels, Komponenten können ihn später überschreiben
    document.title = defaultTitle;
  },
  { immediate: true }
);

// Aufräumen beim Unmount
onBeforeUnmount(() => {
  // Auth-Check-Intervall bereinigen
  if (authCheckInterval) {
    clearInterval(authCheckInterval);
    authCheckInterval = null;
  }

  window.removeEventListener("resize", resizeHandler);
  window.removeEventListener("scroll", handleScroll);

  // Sichtbarkeits-Event-Listener entfernen
  document.removeEventListener('visibilitychange', handleVisibilityChange);

  // Handler aufräumen
  cleanupAnnouncementHandlers();

  // Trenne WebSocket beim Unmount
  WebSocketService.disconnect();
  if (inactivityUpdateInterval) {
    clearInterval(inactivityUpdateInterval);
  }
  if (updateCheckInterval.value) {
    clearInterval(updateCheckInterval.value);
  }
});

const goToServiceStatus = () => {
  router.push("/service-status");
};

// Auf Änderungen der Route reagieren
watch(
  () => route.path,
  () => {
    // Drawer schließen, wenn eine neue Route besucht wird
    drawer.value = false;

    // Announcement-Handler einrichten, wenn nicht auf der Login-Seite
    if (!isLoginPage.value) {
      setupAnnouncementSocket();
    }
  }
);
watch(
  () => authStore.isAuthenticated,
  (isAuthenticated) => {
    if (isAuthenticated) {
      setupInactivityTimer();
    } else {
      if (inactivityUpdateInterval) {
        clearInterval(inactivityUpdateInterval);
      }
    }
  }
);

// Desktop-Modus
const isDesktopMode = ref(false);

const toggleDesktopMode = () => {
  isDesktopMode.value = !isDesktopMode.value;
};

// Neue Funktion zur Überprüfung, ob der Benutzer ausschließlich die retouren_sorting Rolle hat
const isOnlyRetourenSorting = () => {
  const user = authStore.user;
  return (user?.returen_sorting || user?.returen_sorting_v2) && !user?.admin && !user?.super_admin && !user?.developer && !user?.monteur;
};

/** Nutzer hat nur die DTC-Rolle (ohne Monteur/Admin/Sorting). */
const isOnlyDtc = () => {
  const user = authStore.user;
  return (
    !!user?.dtc &&
    !user?.admin &&
    !user?.super_admin &&
    !user?.developer &&
    !user?.monteur &&
    !user?.returen_sorting &&
    !user?.returen_sorting_v2
  );
};
</script>

<style scoped>
/* ======== Grundlegende App-Stile ======== */
.v-app {
  background-color: #121212;
}

.v-main {
  transition: padding 0.3s ease;
  min-height: calc(100vh - 60px);
}

.normal-main {
  padding-top: 76px; /* Mehr Platz für die schwebende Navbar */
}

.mobile-main {
  padding-top: 64px;
}

/* Container-Anpassung: kein zusätzliches Padding mehr nötig */
.main-container {
  padding-bottom: 0;
  min-height: calc(100vh - 64px);
}

/* ======== Hintergrundelemente ======== */
.background-elements {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;
}

.ambient-light {
  position: absolute;
  border-radius: 50%;
  filter: blur(120px);
  opacity: 0.12;
  will-change: transform, opacity;
}

.ambient-light-1 {
  width: 800px;
  height: 800px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.5), rgba(129, 199, 132, 0.2) 40%, transparent 70%);
  top: -200px;
  right: -100px;
  animation: pulse 18s infinite alternate, float 25s ease-in-out infinite;
}

.ambient-light-2 {
  width: 700px;
  height: 700px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.4), rgba(165, 214, 167, 0.2) 40%, transparent 70%);
  bottom: -100px;
  left: -100px;
  animation: pulse 22s infinite alternate-reverse, float 30s ease-in-out infinite reverse;
}

.ambient-light-3 {
  width: 600px;
  height: 600px;
  background: radial-gradient(circle, rgba(76, 175, 80, 0.3), rgba(200, 230, 201, 0.15) 40%, transparent 70%);
  top: 40%;
  left: 50%;
  transform: translate(-50%, -50%);
  animation: pulse 20s infinite alternate, rotate 40s linear infinite;
}

@keyframes pulse {
  0% {
    opacity: 0.08;
    transform: scale(1);
  }
  50% {
    opacity: 0.15;
    transform: scale(1.08);
  }
  100% {
    opacity: 0.08;
    transform: scale(1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translate(0, 0);
  }
  25% {
    transform: translate(30px, -30px);
  }
  50% {
    transform: translate(-20px, 20px);
  }
  75% {
    transform: translate(20px, 30px);
  }
}

@keyframes rotate {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    linear-gradient(rgba(76, 175, 80, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(76, 175, 80, 0.04) 1px, transparent 1px);
  background-size: 30px 30px;
  z-index: 1;
  animation: gridMove 20s linear infinite;
}

@keyframes gridMove {
  0% {
    transform: translate(0, 0);
  }
  100% {
    transform: translate(30px, 30px);
  }
}

/* ======== Navigation - Desktop ======== */
.floating-navbar {
  background: rgba(24, 24, 28, 0.75) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  margin: 12px auto 0;
  max-width: calc(100% - 32px);
  left: 0;
  right: 0;
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow:
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 8px 16px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(76, 175, 80, 0.05);
  will-change: transform, box-shadow;
}

.navbar-scrolled {
  margin: 0;
  max-width: 100%;
  border-radius: 0;
  background: rgba(18, 18, 20, 0.95) !important;
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.3),
    0 8px 40px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(76, 175, 80, 0.1) !important;
  border-bottom: 1px solid rgba(76, 175, 80, 0.15);
}

.navbar-hidden {
  transform: translateY(-120%);
  opacity: 0;
}

.navbar-brand {
  display: flex;
  align-items: center;
  padding: 0 10px;
}

.brand-icon {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.25),
    rgba(76, 175, 80, 0.08)
  );
  border-radius: 12px;
  margin-right: 12px;
  border: 1px solid rgba(76, 175, 80, 0.25);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow:
    0 2px 8px rgba(76, 175, 80, 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.brand-icon:hover {
  transform: scale(1.05) rotate(5deg);
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.app-title {
  font-weight: 700;
  font-size: 1.2rem;
  background: linear-gradient(135deg, #ffffff 0%, #4caf50 50%, #81c784 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  letter-spacing: 0.5px;
  text-shadow: 0 0 20px rgba(76, 175, 80, 0.3);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

.nav-links {
  display: flex;
  gap: 8px;
}

.nav-btn {
  font-weight: 500;
  letter-spacing: 0.3px;
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  padding: 0 16px;
  min-height: 38px;
  text-transform: none;
  box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
}

.nav-btn::before {
  content: "";
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(76, 175, 80, 0.2),
    transparent
  );
  transition: left 0.5s ease;
}

.nav-btn:hover::before {
  left: 100%;
}

.nav-btn::after {
  content: "";
  position: absolute;
  bottom: 0;
  left: 50%;
  width: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    transparent,
    var(--v-theme-success),
    transparent
  );
  transform: translateX(-50%);
  transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  opacity: 0;
  border-radius: 3px;
  box-shadow: 0 0 10px var(--v-theme-success);
}

.nav-btn:hover {
  background-color: rgba(76, 175, 80, 0.12);
  transform: translateY(-2px);
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.2),
    0 2px 6px rgba(76, 175, 80, 0.15);
}

.nav-btn:hover::after {
  width: 85%;
  opacity: 1;
}

.nav-active {
  background: linear-gradient(
    135deg,
    rgba(76, 175, 80, 0.18),
    rgba(76, 175, 80, 0.12)
  ) !important;
  font-weight: 600;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 2px 8px rgba(76, 175, 80, 0.2);
}

.nav-active::after {
  width: 85%;
  opacity: 1;
}

.dropdown-menu {
  background: rgba(30, 30, 34, 0.95) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(76, 175, 80, 0.15);
  border-radius: 12px;
  overflow: hidden;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 16px 48px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(76, 175, 80, 0.1);
  animation: dropdownSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes dropdownSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dropdown-item {
  transition: all 0.2s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 8px;
  margin: 4px;
  position: relative;
  overflow: hidden;
}

.dropdown-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 3px;
  height: 100%;
  background: var(--v-theme-success);
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.dropdown-item:hover {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.08)
  );
  transform: translateX(4px);
}

.dropdown-item:hover::before {
  transform: scaleY(1);
}

/* ======== Navigation - Mobile ======== */
.mobile-navbar {
  background: rgba(18, 18, 20, 0.95) !important;
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid rgba(76, 175, 80, 0.1);
  transition: transform 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.menu-icon {
  color: rgba(255, 255, 255, 0.9);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.menu-icon:hover {
  color: rgba(76, 175, 80, 1);
  transform: scale(1.1) rotate(90deg);
}

.brand-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mobile-drawer {
  background: linear-gradient(135deg, #1a1a20, #121216) !important;
  border-right: 1px solid rgba(76, 175, 80, 0.15);
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.3);
}

.drawer-header {
  padding: 24px 16px;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.25), rgba(76, 175, 80, 0.05));
  border-bottom: 1px solid rgba(76, 175, 80, 0.2);
}

.user-profile {
  display: flex;
  align-items: center;
  padding: 8px;
}

.drawer-avatar {
  margin-right: 16px;
  font-size: 1.5rem;
  font-weight: 600;
  border: 2px solid rgba(76, 175, 80, 0.4);
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.3),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
}

.user-info {
  display: flex;
  flex-direction: column;
}

.user-name {
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.user-role {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
}

.drawer-nav {
  padding: 8px;
}

.menu-item {
  margin-bottom: 4px;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.menu-item::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, var(--v-theme-success), rgba(129, 199, 132, 0.5));
  transform: scaleY(0);
  transition: transform 0.3s ease;
}

.menu-item:hover {
  background: linear-gradient(
    90deg,
    rgba(76, 175, 80, 0.15),
    rgba(76, 175, 80, 0.05)
  );
  transform: translateX(6px);
}

.menu-item:hover::before {
  transform: scaleY(1);
}

.submenu-items {
  padding-left: 24px;
  margin-bottom: 8px;
}

.submenu-item {
  border-radius: 8px;
  margin-bottom: 2px;
}

.drawer-footer {
  padding: 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

/* ======== Benutzerbereich ======== */
.user-section {
  display: flex;
  align-items: center;
}

.user-greeting {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  margin-right: 16px;
  display: flex;
  align-items: center;
}

.user-name {
  font-weight: 600;
  margin-left: 4px;
  color: white;
}

.message-icon-btn {
  background: transparent;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
}

.message-icon-btn::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.4), rgba(100, 181, 246, 0.2));
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.message-icon-btn:hover {
  transform: scale(1.15) translateY(-2px);
}

.message-icon-btn:hover::before {
  opacity: 1;
  animation: pulse-ring 1.5s ease-out infinite;
}

.user-avatar-btn {
  background: transparent;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
}

.user-avatar-btn::before {
  content: "";
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.4), rgba(129, 199, 132, 0.2));
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: -1;
}

.user-avatar-btn:hover {
  transform: scale(1.1) rotate(5deg);
}

.user-avatar-btn:hover::before {
  opacity: 1;
  animation: pulse-ring 1.5s ease-out infinite;
}

@keyframes pulse-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.3);
    opacity: 0;
  }
}

.user-avatar {
  border: 2px solid rgba(76, 175, 80, 0.4);
  font-weight: 600;
  font-size: 1rem;
  box-shadow:
    0 2px 8px rgba(76, 175, 80, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
}

.user-avatar-btn:hover .user-avatar {
  border-color: rgba(76, 175, 80, 0.6);
  box-shadow:
    0 4px 16px rgba(76, 175, 80, 0.4),
    inset 0 1px 0 rgba(255, 255, 255, 0.15);
}

.user-menu {
  background: rgba(30, 30, 40, 0.98) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(76, 175, 80, 0.15);
  overflow: hidden;
  border-radius: 16px;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 16px 64px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(76, 175, 80, 0.1);
  animation: menuSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes menuSlideIn {
  0% {
    opacity: 0;
    transform: translateY(-15px) scale(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.status-section {
  background: linear-gradient(
    to right bottom,
    rgba(35, 35, 45, 0.95),
    rgba(25, 25, 35, 0.98)
  );
}

.status-header {
  padding: 16px;
}

.user-fullname {
  font-size: 0.95rem;
  font-weight: 500;
}

.user-status-row {
  display: flex;
  align-items: center;
  font-size: 0.8rem;
  opacity: 0.9;
}

.status-label {
  margin-left: 4px;
}

/* Verbesserte Status-Liste */
.status-list {
  background: transparent !important;
  padding: 4px 0;
}

.status-list-item {
  margin: 4px 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.status-list-item:hover {
  background-color: rgba(76, 175, 80, 0.1) !important;
}

.status-active {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.status-avatar {
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.custom-status-input {
  margin-top: 8px;
  display: flex;
  align-items: center;
}

.emoji-list {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 4px;
}

.emoji-item {
  cursor: pointer;
  text-align: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.emoji-item:hover {
  background-color: rgba(76, 175, 80, 0.1);
}

/* ======== Footer ======== */
.modern-footer {
  width: 100%;
  background: rgba(24, 24, 28, 0.85) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border-top: 1px solid rgba(76, 175, 80, 0.08);
  padding: 10px 24px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.85rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: fixed;
  bottom: 0;
  left: 0;
  z-index: 5;
  box-shadow:
    0 -4px 16px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(76, 175, 80, 0.05);
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
}

.footer-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.footer-logo {
  display: flex;
  align-items: center;
}

.footer-brand {
  font-weight: 600;
  margin-left: 8px;
  letter-spacing: 0.5px;
  font-size: 0.95rem;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 12px;
}

.dot-divider {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.3);
}

.footer-link {
  color: rgba(255, 255, 255, 0.6);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  padding: 4px 8px;
  border-radius: 6px;
}

.footer-link:after {
  content: "";
  position: absolute;
  bottom: 2px;
  left: 8px;
  width: 0;
  height: 2px;
  background: linear-gradient(to right, var(--v-theme-success), rgba(129, 199, 132, 0.5));
  transition: width 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 0 8px var(--v-theme-success);
}

.footer-link:hover {
  color: rgba(76, 175, 80, 1);
  transform: translateY(-2px);
  background: rgba(76, 175, 80, 0.08);
}

.footer-link:hover:after {
  width: calc(100% - 16px);
}

.footer-copyright {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.5);
  display: flex;
  align-items: center;
  gap: 5px;
}

/* Herzschlag-Animation */
.heartbeat-icon {
  animation: heartbeat 1.3s ease-in-out infinite;
  transform-origin: center;
  margin: 0 2px;
}

@keyframes heartbeat {
  0% {
    transform: scale(1);
  }
  14% {
    transform: scale(1.3);
  }
  28% {
    transform: scale(1);
  }
  42% {
    transform: scale(1.3);
  }
  70% {
    transform: scale(1);
  }
}

/* ======== Dialoge ======== */
.dialog-card {
  background: rgba(30, 30, 40, 0.95) !important;
  backdrop-filter: blur(24px) saturate(180%);
  -webkit-backdrop-filter: blur(24px) saturate(180%);
  border: 1px solid rgba(76, 175, 80, 0.15);
  border-radius: 16px;
  overflow: hidden;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.4),
    0 32px 96px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(76, 175, 80, 0.1);
  animation: dialogAppear 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes dialogAppear {
  0% {
    opacity: 0;
    transform: scale(0.9) translateY(20px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.dialog-title {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.05));
  border-bottom: 1px solid rgba(76, 175, 80, 0.25);
  padding: 16px;
  font-weight: 600;
  position: relative;
  box-shadow: 0 2px 8px rgba(76, 175, 80, 0.1);
}

.dialog-close-btn {
  position: absolute !important;
  top: 8px !important;
  right: 8px !important;
  opacity: 0.7;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-radius: 8px;
}

.dialog-close-btn:hover {
  opacity: 1;
  background-color: rgba(255, 59, 48, 0.15);
  transform: scale(1.1) rotate(90deg);
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.1);
}

.dialog-content {
  padding: 16px;
  color: rgba(255, 255, 255, 0.9);
}

.dialog-content-scrollable {
  overflow-y: auto;
  max-height: calc(90vh - 120px);
  padding: 16px;
}

.dialog-actions {
  padding: 8px 16px 16px;
}

.submit-btn {
  box-shadow:
    0 4px 12px rgba(76, 175, 80, 0.3),
    0 2px 6px rgba(76, 175, 80, 0.2);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  position: relative;
  overflow: hidden;
}

.submit-btn::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
}

.submit-btn:hover::before {
  width: 300px;
  height: 300px;
}

.submit-btn:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow:
    0 8px 20px rgba(76, 175, 80, 0.4),
    0 4px 12px rgba(76, 175, 80, 0.3);
}

.submit-btn:active {
  transform: translateY(0) scale(0.98);
}

/* ======== Snackbar ======== */
.modern-snackbar {
  border-radius: 12px;
  margin-bottom: 60px;
  background: rgba(30, 30, 40, 0.95) !important;
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(0, 0, 0, 0.2),
    0 0 0 1px rgba(76, 175, 80, 0.2);
  border: 1px solid rgba(76, 175, 80, 0.15);
  animation: snackbarSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes snackbarSlideIn {
  0% {
    opacity: 0;
    transform: translateY(100px) scale(0.8);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* ======== Loading Bar ======== */
.loading-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
}

/* ======== Announcement Toast System ======== */
.announcement-container {
  position: fixed;
  bottom: 24px;
  right: 24px;
  max-width: 400px;
  width: calc(100% - 48px);
  z-index: 9999;
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.announcement-toast {
  backdrop-filter: blur(16px) saturate(180%);
  pointer-events: auto;
  overflow: hidden;
  border-radius: 16px;
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border-left: 4px solid transparent;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.4),
    0 6px 16px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  position: relative;
}

.announcement-toast::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), transparent);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
}

.announcement-toast:hover::before {
  opacity: 1;
}

.announcement-toast:hover {
  transform: translateX(-4px) scale(1.02);
  box-shadow:
    0 16px 40px rgba(0, 0, 0, 0.5),
    0 8px 20px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

.announcement-toast.announcement {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.95), rgba(129, 199, 132, 0.9)) !important;
  border-left-color: #a5d6a7;
}

.announcement-toast.warning {
  background: linear-gradient(135deg, rgba(211, 47, 47, 0.95), rgba(244, 67, 54, 0.9)) !important;
  border-left-color: #ff8a80;
}

.announcement-toast-content {
  display: flex;
  padding: 16px;
  gap: 16px;
  align-items: flex-start;
}

.announcement-icon {
  flex-shrink: 0;
}

.announcement-text-container {
  flex: 1;
}

.announcement-title {
  font-weight: 600;
  margin-bottom: 6px;
  color: white;
  font-size: 0.95rem;
}

.announcement-message {
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  line-height: 1.5;
}

.announcement-actions {
  padding: 8px 16px 16px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.dismiss-btn {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.dismiss-btn:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

.warning-btn {
  font-weight: 500;
}

/* Animation für Announcements */
.announcement-transition-enter-active,
.announcement-transition-leave-active {
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.announcement-transition-enter-from {
  opacity: 0;
  transform: translateX(120px) scale(0.8) rotateY(20deg);
}

.announcement-transition-leave-to {
  opacity: 0;
  transform: translateY(60px) scale(0.8);
}

/* Eingeklappter Zustand der Announcement-Karte */
.announcement-toast.collapsed {
  max-height: 50px;
  overflow: hidden;
}

.announcement-toast.collapsed .announcement-toast-content {
  padding: 8px 16px;
}

.announcement-toast .collapse-toggle {
  position: absolute;
  right: 8px;
  top: 8px;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.announcement-toast .collapse-toggle:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

/* ======== KV-Benachrichtigungs-Popup ======== */
.kv-notification-container {
  position: fixed;
  bottom: 90px;
  right: 20px;
  z-index: 2001;
  pointer-events: none;
}

.kv-notification-popup {
  min-width: 320px;
  max-width: 400px;
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15), rgba(25, 118, 210, 0.1));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(33, 150, 243, 0.3);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(33, 150, 243, 0.2);
  pointer-events: auto;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.kv-notification-popup::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: linear-gradient(90deg, #2196F3, #1976D2);
  opacity: 0.8;
}

.kv-notification-popup:hover::before {
  height: 4px;
}

.kv-notification-popup:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 40px rgba(33, 150, 243, 0.3);
}

.kv-notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.kv-notification-icon {
  flex-shrink: 0;
  animation: kvPulse 2s ease-in-out infinite;
}

@keyframes kvPulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

.kv-notification-text-container {
  flex: 1;
  min-width: 0;
}

.kv-notification-title {
  font-weight: 600;
  font-size: 0.95rem;
  color: #fff;
  margin-bottom: 4px;
}

.kv-notification-message {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.4;
}

.kv-notification-actions {
  padding: 8px 16px 12px;
  background: rgba(0, 0, 0, 0.1);
}

.kv-notification-popup.collapsed {
  min-width: 250px;
}

.kv-notification-popup.collapsed .kv-notification-content {
  padding: 12px 16px;
}

.kv-notification-popup .collapse-toggle {
  flex-shrink: 0;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.kv-notification-popup .collapse-toggle:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.1);
}

/* Transitions für KV-Notification */
.kv-notification-transition-enter-active,
.kv-notification-transition-leave-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.kv-notification-transition-enter-from {
  transform: translateX(400px);
  opacity: 0;
}

.kv-notification-transition-leave-to {
  transform: translateX(400px);
  opacity: 0;
}

/* ======== Mobile Anpassungen ======== */
.mobile-status-container {
  background: rgba(25, 25, 30, 0.4);
  border-radius: 8px;
  padding: 12px;
}

/* ======== Responsive Anpassungen ======== */
@media (max-width: 960px) {
  .modern-footer {
    position: relative; /* Nicht mehr fixiert */
    margin-top: 20px; /* Abstand zum Inhalt */
    z-index: 1; /* Niedrigerer z-index */
  }

  .footer-content {
    flex-direction: column;
    gap: 10px;
  }

  .footer-left {
    flex-direction: column;
    gap: 8px;
  }

  .footer-logo {
    justify-content: center;
  }

  .footer-copyright {
    justify-content: center;
  }

  .footer-links {
    justify-content: center;
  }

  /* KV-Notification mobile Anpassung */
  .kv-notification-container {
    bottom: 20px;
    right: 10px;
    left: 10px;
  }

  .kv-notification-popup {
    min-width: unset;
    width: 100%;
  }

  .announcement-container {
    bottom: 20px;
    right: 10px;
    left: 10px;
  }

  .announcement-toast {
    min-width: unset;
    width: 100%;
  }
}

@media (max-width: 600px) {
  .announcement-container {
    bottom: 16px;
    right: 16px;
    width: calc(100% - 32px);
    max-width: none;
  }

  .footer-link {
    font-size: 0.75rem;
    padding: 4px;
  }

  .dialog-content-scrollable {
    max-height: calc(90vh - 100px);
  }
}

/* Nicht-scoped-Stile für die Sidebar-Anpassung */
.sidebar-is-open .v-main {
  transition: padding-right 0.3s ease;
  padding-right: 350px !important;
}

@media (max-width: 1200px) {
  .sidebar-is-open .v-main {
    padding-right: 320px !important;
  }
}

@media (max-width: 992px) {
  .sidebar-is-open .v-main {
    padding-right: 280px !important;
  }
}

@media (max-width: 768px) {
  .sidebar-is-open .v-main {
    padding-right: 260px !important;
  }
}

@media (max-width: 576px) {
  .sidebar-is-open .v-main {
    padding-right: 0 !important;
  }
}

.desktop-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  background: linear-gradient(135deg, rgba(30, 30, 40, 0.95), rgba(24, 24, 32, 0.95));
  backdrop-filter: blur(16px) saturate(180%);
  border-radius: 50%;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.3),
    0 4px 12px rgba(76, 175, 80, 0.2),
    0 0 0 1px rgba(76, 175, 80, 0.3);
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 2px solid rgba(76, 175, 80, 0.2);
  animation: float-button 3s ease-in-out infinite;
}

@keyframes float-button {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
}

.desktop-toggle:hover {
  transform: scale(1.15) rotate(10deg);
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.3), rgba(129, 199, 132, 0.2));
  box-shadow:
    0 12px 32px rgba(76, 175, 80, 0.4),
    0 6px 16px rgba(76, 175, 80, 0.3),
    0 0 0 2px rgba(76, 175, 80, 0.5);
  border-color: rgba(76, 175, 80, 0.5);
  animation: none;
}

.desktop-toggle:active {
  transform: scale(1.05) rotate(5deg);
}

/* ======== Reduced Motion für Barrierefreiheit ======== */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  .ambient-light,
  .grid-overlay,
  .desktop-toggle {
    animation: none !important;
  }
}
</style>
